import { EnumSchema, FromSchema, ZodDate } from '@40seas/entities/common';
import { AddressSchema, IdentificationSchema } from '@40seas/models';
import { z } from 'zod';
import { ExactMatchType } from './exact-match-type.enum';
import { Provider } from './identity-provider.types';

/** Schema for score breakdown - explains how a candidate's score was calculated */
export const ScoreBreakdownSchema = z
    .object({
        /** Points from name similarity (0-30) */
        namePoints: z.number().openapi({ description: 'Points from name similarity (0-30)' }),
        /** Name similarity percentage (0-100) */
        nameSimilarity: z.number().openapi({ description: 'Name similarity percentage (0-100)' }),
        /** Points from city match (0 or 15) */
        cityPoints: z.number().openapi({ description: 'Points from city match (0 or 15)' }),
        /** Points from address partial match (0 or 5) */
        addressPoints: z.number().openapi({ description: 'Points from address partial match (0 or 5)' }),
        /** Whether country mismatch penalty was applied */
        countryMismatch: z.boolean().openapi({ description: 'Whether country mismatch penalty was applied' }),
        /** Raw score before capping */
        rawScore: z.number().openapi({ description: 'Raw score before capping' }),
        /** Final score after capping */
        finalScore: z.number().openapi({ description: 'Final score after capping' }),
        /** Human-readable explanation */
        reason: z.string().openapi({ description: 'Human-readable explanation of the score' }),
    })
    .strict()
    .openapi({ description: 'Breakdown explaining how a candidate score was calculated' });

export type ScoreBreakdown = z.infer<typeof ScoreBreakdownSchema>;

export const IdentityCandidateSchema = z
    .object({
        /**
         * Unique key for deduplication across providers
         * Format: "REG:{type}:{code}" or "NAME:{country}:{normalizedName}"
         */
        candidateKey: z.string().openapi({
            description: 'Unique key for deduplication across providers. Format: REG:{type}:{code} or NAME:{country}:{normalizedName}',
        }),

        /** Legal name of the business */
        legalName: z.string().openapi({ description: 'Legal name of the business entity' }),

        /** Business address */
        address: AddressSchema.optional().openapi({ description: 'Physical address of the business' }),

        /**
         * All identification numbers for this business
         * Examples: DUNS, CRN (UK), SIREN (FR), KVK (NL), EIN (US)
         */
        identifications: z
            .array(IdentificationSchema)
            .optional()
            .openapi({ description: 'All identification numbers for this business (DUNS, CRN, SIREN, KVK, EIN, etc.)' }),

        /** Date when business started operations */
        businessStartDate: ZodDate.optional().openapi({
            format: 'date-time',
            type: 'string',
            description: 'Date when the business started operations',
        }),

        /** Type of business entity (Ltd, PLC, GmbH, etc.) */
        businessType: z.string().optional().openapi({ description: 'Type of business entity (Ltd, PLC, GmbH, etc.)' }),

        /** Company website */
        website: z.string().optional().openapi({ description: 'Company website URL' }),

        /** Which data providers returned this candidate */
        providers: z
            .array(z.nativeEnum(Provider))
            .default([])
            .openapi({ description: 'Data providers that returned this candidate (Allianz, Atradius, CreditSafe, WiserFunding, Aiprize)' }),

        /** Type of exact match found (if any) */
        exactMatchType: EnumSchema(ExactMatchType).openapi({
            description: 'Type of exact match found between buyer and candidate',
        }),

        /**
         * Calculated similarity score (0-100)
         * - 100: Registration number or exact name match
         * - 90: Exact address match
         * - 50-80: Similarity match (capped at 50 for multiple candidates)
         * - 0: No match
         */
        score: z.number().min(0).max(100).openapi({
            description: 'Calculated similarity score (0-100). 100 = exact match, 90 = address match, 50-80 = similarity match',
        }),

        /** Breakdown explaining how the score was calculated */
        scoreBreakdown: ScoreBreakdownSchema.optional().openapi({
            description: 'Detailed breakdown of how the score was calculated',
        }),
    })
    .strict()
    .openapi({
        description:
            'A candidate business identity found from data providers. Used for matching and deduplication during identity verification.',
    });

export class IdentityCandidate extends FromSchema(IdentityCandidateSchema) {
    candidateKey: string;
    legalName: string;
    address?: any;
    identifications?: { type: string; code: string }[];
    businessStartDate?: Date;
    businessType?: string;
    website?: string;
    providers: Provider[];
    exactMatchType: ExactMatchType;
    score: number;
    scoreBreakdown?: ScoreBreakdown;
}
